// Team ACTIONS (Team activity page, MANAGE tier) — what each person actually
// did in the app, read from public.audit_events.
//
// WHY THIS IS NOT A ROW COUNT. One stage move writes several audit rows in the
// same second: the rpc_set_stage row ("Stage changed to <stage>", details
// {from_stage,to_stage,reason}, event_source 'app') plus the generic trigger's
// column-level rows ("cases updated: current_stage", "cases updated:
// next_action_due_at", "tasks row created", event_source 'data_change'). A
// table that counted rows would credit a nurse with four pieces of work for
// one click, and would credit the scheduled job with hundreds a day. So rows
// are grouped into STEPS — one actor, one patient, one moment — and each step
// is classified once (groupSteps). The table counts steps.
//
// THE SCHEDULED JOB IS NOT A PERSON. The server-side job writes hundreds of
// 'data_changed' rows every morning with actor_user_id NULL. Those rows are
// never attributed to anyone: the window query excludes them, a separate
// head-count query counts them, and the panel shows that count on its own
// muted line.
//
// Data rules (house style): every loader returns the {ok,data,error,code}
// envelope and never throws; nothing is joined in SQL — actors are named from
// the already-loaded users directory, patients from the loaded cases slice or a
// minimal case_id/reference_number/patient_name select for the ids seen. Only
// small, named fields are pulled out of `details` (PostgREST JSON-path select):
// the trigger rows carry the full before/after case row there, and dragging
// that across for every row in a four-week window would be slow and would move
// far more patient data than this page shows.

import { getClient, ok, fail } from '../db/client.js';
import { TABLES } from '../db/contract.js';
import { allCases } from './cases.js';

// ─── Columns ───────────────────────────────────────────────────────────────

/** Window query: just enough to group and classify. No free text beyond `summary`. */
export const ACTION_COLS =
  'event_id,case_id,occurred_at,actor_user_id,event_type,event_source,summary,related_table,' +
  'from_stage:details->>from_stage,to_stage:details->>to_stage';

/** Person query (on demand): adds the typed reason and the few fields the description uses. */
export const PERSON_ACTION_COLS =
  ACTION_COLS +
  ',reason:details->>reason,outcome:details->>outcome,channel:details->>channel,task_title:details->after->>title';

/** Rows per page when reading a window (the PostgREST page cap). */
const PAGE = 1000;
/** Hard stop for one window read — four weeks of a busy team is well under this. */
export const ACTIONS_ROW_CAP = 20000;
/** The person list shows at most this many audit rows, newest first. */
export const PERSON_ROW_CAP = 500;
/** case_id IN (…) lists are chunked so the request URL stays short. */
const CASE_CHUNK = 100;

/** The reason rpc_set_stage is given when the nurse typed nothing (cases.js setStage). */
export const DEFAULT_STAGE_REASON = 'Manual stage change';

// ─── Weeks (Nairobi days, Monday start) ────────────────────────────────────

/** East Africa Time is UTC+3 all year (Kenya keeps no daylight saving). */
const NAIROBI_OFFSET_MS = 3 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

export const WEEK_CHOICES = Object.freeze([
  Object.freeze({ id: 'this', label: 'This week' }),
  Object.freeze({ id: 'last', label: 'Last week' }),
  Object.freeze({ id: '4w', label: '4 weeks' }),
]);

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** A Date whose UTC fields read as Nairobi wall-clock time. @param {number} ms */
function nbo(ms) {
  return new Date(ms + NAIROBI_OFFSET_MS);
}

/**
 * Pure: the [from, to) window for a picker choice. Weeks start Monday 00:00 in
 * Nairobi, whatever the browser's own timezone. 'this' runs to next Monday
 * (rows in the future do not exist, so the open end costs nothing), 'last' is
 * the previous Monday-to-Monday, '4w' is this week plus the three before it.
 * Unknown choices fall back to 'this'.
 * @param {string} choice 'this' | 'last' | '4w'
 * @param {Date} [now]
 * @returns {{id: string, from: string, to: string, label: string, phrase: string, range: string, fileTag: string}}
 */
export function weekWindow(choice, now) {
  const id = choice === 'last' || choice === '4w' ? choice : 'this';
  const t = now instanceof Date && !isNaN(now.getTime()) ? now.getTime() : Date.now();
  const local = nbo(t);
  const sinceMon = (local.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  const monLocal = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate()) - sinceMon * DAY_MS;
  const thisMon = monLocal - NAIROBI_OFFSET_MS; // back to a real instant
  const from = id === 'last' ? thisMon - 7 * DAY_MS : id === '4w' ? thisMon - 21 * DAY_MS : thisMon;
  const to = id === 'last' ? thisMon : thisMon + 7 * DAY_MS;
  const day = (/** @type {number} */ ms) => {
    const d = nbo(ms);
    return `${DOW[d.getUTCDay()]} ${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
  };
  const iso = (/** @type {number} */ ms) => nbo(ms).toISOString().slice(0, 10);
  const label = (WEEK_CHOICES.find((w) => w.id === id) || WEEK_CHOICES[0]).label;
  return {
    id,
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
    label,
    phrase: id === 'this' ? 'this week' : id === 'last' ? 'last week' : 'in the last 4 weeks',
    range: `${day(from)} – ${day(to - DAY_MS)}`,
    fileTag: `${iso(from)}_to_${iso(to - DAY_MS)}`,
  };
}

/**
 * Pure: a timestamp as Nairobi wall-clock text ("Mon 19 Oct, 14:05"), so the
 * page reads the same on any device and in any test runner.
 * @param {any} iso @param {{time?: boolean, date?: boolean}} [opts]
 * @returns {string} '—' for anything unparseable
 */
export function fmtNairobi(iso, opts) {
  const ms = Date.parse(iso);
  if (!iso || isNaN(ms)) return '—';
  const d = nbo(ms);
  const p = (/** @type {number} */ x) => String(x).padStart(2, '0');
  const date = `${DOW[d.getUTCDay()]} ${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
  const time = `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
  const o = opts || {};
  if (o.time === false) return date;
  if (o.date === false) return time;
  return `${date}, ${time}`;
}

// ─── Grouping rows into steps ──────────────────────────────────────────────

/** Rows this close to a step's first row (same actor, same patient) belong to it. */
export const STEP_WINDOW_MS = 2000;

const STAGE_RE = /^stage changed to\b/i;

/** @param {any} r @returns {boolean} */
function isStageRow(r) {
  return STAGE_RE.test(String((r && r.summary) || ''));
}

/** Event types the app/RPCs write explicitly, in the order they win a mixed step. */
const TYPE_KIND = [
  ['case_archived', 'archive'],
  ['consent_promoted', 'consent'],
  ['case_created', 'case_created'],
  ['adverse_event_reported', 'ae'],
  ['contact_logged', 'contact'],
  ['case_assigned', 'assign'],
  ['expense_created', 'expense'],
  ['expense_deleted', 'expense'],
  ['escalation_created', 'escalation'],
  ['sha_application_created', 'sha'],
  ['task_completed', 'task_done'],
  ['task_created', 'task'],
  ['case_stage_changed', 'stage'],
  ['stage_change', 'stage'],
];

/** Summary patterns, for steps made only of 'data_changed' rows. First match wins. */
const SUMMARY_KIND = [
  [/\bcase notes updated\b/i, 'note'],
  [/\bupdated clinical details\b/i, 'clinical'],
  [/\b(changed role for|app access for|two-step verification)\b/i, 'admin'],
  [/^cases updated:.*\b(is_archived|archived_at)\b/i, 'archive'],
  [/^cases updated:.*\bassigned_to\b/i, 'assign'],
  [/^cases updated:.*\bnotes\b/i, 'note'],
  [/^cases updated:.*\bcurrent_stage\b/i, 'stage'],
  [/^tasks row created\b/i, 'task'],
  [/^tasks updated:.*\bstatus\b/i, 'task_done'],
  [/^contacts row created\b/i, 'contact'],
  [/^adverse_events row created\b/i, 'ae'],
  [/^expenses row created\b/i, 'expense'],
  [/^escalations row created\b/i, 'escalation'],
];

/** Which table column each kind is counted under. Everything else is "Other". */
export const KIND_COLUMN = Object.freeze({ stage: 'stage', note: 'note', task: 'task' });

/**
 * Pure: classify one step from its rows.
 * @param {any[]} rows
 * @returns {{kind: string, fromStage: string|null, toStage: string|null}}
 */
export function classifyRows(rows) {
  const list = rows || [];
  const stage = list.find(isStageRow);
  if (stage) {
    const from = stage.from_stage || null;
    const to = stage.to_stage || String(stage.summary).replace(STAGE_RE, '').trim() || null;
    // A same-stage "move" changes nothing on the board: what the nurse really
    // did was write a reason against the patient. Counted as a note.
    const kind = from && to && from === to ? 'note' : 'stage';
    return { kind, fromStage: from, toStage: to };
  }
  for (const [type, kind] of TYPE_KIND) {
    if (list.some((r) => r && r.event_type === type)) return { kind, fromStage: null, toStage: null };
  }
  for (const [re, kind] of SUMMARY_KIND) {
    if (list.some((r) => r && /** @type {RegExp} */ (re).test(String(r.summary || '')))) {
      return { kind: /** @type {string} */ (kind), fromStage: null, toStage: null };
    }
  }
  return { kind: 'other', fromStage: null, toStage: null };
}

/**
 * Pure: group audit rows into real steps. A step is one actor + one patient
 * within STEP_WINDOW_MS of the step's first row — "the same second", with a
 * second's grace for a follow-up statement that lands just after the
 * transaction (setStage's on-hold lift, a client-stamped app row). The patient
 * is part of the key so a bulk assignment of twenty patients in one click
 * reads as twenty pieces of work, not one. A second "Stage changed to" row
 * always opens a new step rather than being swallowed by the first.
 *
 * actor null (the scheduled job) is grouped the same way and kept as
 * actor: null — perPersonStats() is where it is set aside.
 * @param {any[]} rows audit_events rows (any order; junk tolerated)
 * @returns {Array<{id: string, actor: string|null, caseId: string|null, at: string, last: string, rows: any[], kind: string, fromStage: string|null, toStage: string|null}>} newest first
 */
export function groupSteps(rows) {
  const clean = (rows || []).filter((r) => r && typeof r === 'object' && !isNaN(Date.parse(r.occurred_at)));
  const keyed = clean
    .map((r) => ({ r, t: Date.parse(r.occurred_at), a: r.actor_user_id || null, c: r.case_id || null }))
    .sort((x, y) => {
      const a = String(x.a || '').localeCompare(String(y.a || ''));
      if (a) return a;
      const c = String(x.c || '').localeCompare(String(y.c || ''));
      if (c) return c;
      return x.t - y.t;
    });
  /** @type {any[]} */
  const steps = [];
  /** @type {any} */
  let cur = null;
  for (const k of keyed) {
    const joins =
      cur &&
      cur.actor === k.a &&
      cur.caseId === k.c &&
      k.t - cur.t0 <= STEP_WINDOW_MS &&
      !(isStageRow(k.r) && cur.rows.some(isStageRow));
    if (joins) {
      cur.rows.push(k.r);
      cur.t1 = k.t;
    } else {
      cur = { actor: k.a, caseId: k.c, t0: k.t, t1: k.t, rows: [k.r] };
      steps.push(cur);
    }
  }
  return steps
    .map((s) => {
      const c = classifyRows(s.rows);
      return {
        id: String(s.rows[0].event_id || `${s.actor || 'system'}:${s.caseId || '-'}:${s.t0}`),
        actor: s.actor,
        caseId: s.caseId,
        at: new Date(s.t0).toISOString(),
        last: new Date(s.t1).toISOString(),
        rows: s.rows,
        kind: c.kind,
        fromStage: c.fromStage,
        toStage: c.toStage,
      };
    })
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

// ─── Per-person table ──────────────────────────────────────────────────────

/** Roles that run the programme rather than hold patients (users.js assignableUsers). */
const MANAGER_ROLE_RE = /admin|programmes/i;

/** @param {any} role @returns {boolean} */
export function isNurseRole(role) {
  return !MANAGER_ROLE_RE.test(String(role || ''));
}

/**
 * Pure: one row per ACTIVE staff member — zeros included, because a nurse who
 * did nothing this week is exactly the row an administrator is looking for —
 * plus anyone else whose work appears in the window (deactivated mid-week, or
 * an id the directory does not know), so no recorded step is ever dropped.
 * Steps with no actor go to `system`, never to a person.
 * @param {any[]} steps from groupSteps
 * @param {any[]} users app_users rows ({user_id, full_name, role, is_active})
 * @returns {{people: Array<{userId: string, name: string, role: string, active: boolean, known: boolean, nurse: boolean, patients: number, stage: number, note: number, task: number, other: number, steps: number, last: string|null}>, system: {steps: number, rows: number}}}
 */
export function perPersonStats(steps, users) {
  /** @type {Record<string, any>} */
  const by = {};
  const dir = /** @type {Record<string, any>} */ ({});
  for (const u of users || []) if (u && u.user_id) dir[u.user_id] = u;
  const make = (/** @type {string} */ id) => {
    const u = dir[id];
    return {
      userId: id,
      name: (u && u.full_name) || 'Unknown user',
      role: (u && u.role) || '',
      active: !!u && u.is_active !== false,
      known: !!u,
      nurse: !!u && isNurseRole(u.role),
      cases: new Set(),
      patients: 0,
      stage: 0,
      note: 0,
      task: 0,
      other: 0,
      steps: 0,
      last: /** @type {string|null} */ (null),
    };
  };
  for (const u of Object.values(dir)) if (u.is_active !== false) by[u.user_id] = make(u.user_id);
  const system = { steps: 0, rows: 0 };
  for (const s of steps || []) {
    if (!s) continue;
    if (!s.actor) {
      system.steps++;
      system.rows += (s.rows || []).length;
      continue;
    }
    const p = by[s.actor] || (by[s.actor] = make(s.actor));
    p.steps++;
    const col = /** @type {Record<string, string>} */ (KIND_COLUMN)[s.kind] || 'other';
    p[col]++;
    if (s.caseId) p.cases.add(s.caseId);
    if (!p.last || s.at > p.last) p.last = s.at;
  }
  const people = Object.values(by).map((p) => {
    const { cases, ...rest } = p;
    return Object.assign(rest, { patients: cases.size });
  });
  people.sort((a, b) => b.patients - a.patients || String(a.name).localeCompare(String(b.name)));
  return { people, system };
}

// ─── Words ─────────────────────────────────────────────────────────────────

const UPPER_WORDS = new Set(['sha', 'ae', 'pv', 'id']);

/**
 * Pure: a case_stage enum as words — 'sha_application_due' → 'SHA application
 * due', 'adherence_day15_due' → 'Adherence day 15 due'. Unknown/empty → '—'.
 * @param {any} v @returns {string}
 */
export function stageLabel(v) {
  const s = String(v || '').trim();
  if (!s) return '—';
  const words = s
    .replace(/([a-z])(\d)/gi, '$1 $2')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => (UPPER_WORDS.has(w.toLowerCase()) ? w.toUpperCase() : w.toLowerCase()));
  if (!words.length) return '—';
  if (words[0] === words[0].toLowerCase()) words[0] = words[0][0].toUpperCase() + words[0].slice(1);
  return words.join(' ');
}

/** 'reached_patient' → 'reached patient'. @param {any} v @returns {string} */
function words(v) {
  return String(v || '').replace(/_/g, ' ').trim();
}

/**
 * Pure: hide a patient's name inside free text. The person list may be on a
 * shared screen, and nurses type names into reasons ("Called Mary twice").
 * Replaces the full name and each name part of three letters or more,
 * case-insensitively. No name, or names shown → text unchanged.
 * @param {any} text @param {any} name @returns {string}
 */
export function maskName(text, name) {
  let out = String(text ?? '');
  const n = String(name || '').trim();
  if (!out || !n) return out;
  const parts = [n, ...n.split(/\s+/)].filter((p) => p.length >= 3);
  parts.sort((a, b) => b.length - a.length);
  for (const p of parts) {
    const re = new RegExp(p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    out = out.replace(re, '[name]');
  }
  return out;
}

/**
 * "Jane Wanjiru — assigned patient to Grace" → "assigned patient to Grace".
 * logAudit prepends the actor's name only when it has one, so the prefix is
 * stripped only when it reads as a name (every word capitalised or in
 * brackets) — "updated clinical details — diagnosis" keeps its first half.
 * @param {any} summary @returns {string}
 */
function stripActor(summary) {
  const s = String(summary || '');
  const m = /^(.{1,80}?)\s—\s/.exec(s);
  if (m && m[1].split(/\s+/).every((w) => /^[A-Z(]/.test(w))) return s.slice(m[0].length);
  return s;
}

/** First non-empty field across a step's rows. @param {any[]} rows @param {string} f */
function field(rows, f) {
  for (const r of rows || []) {
    const v = r && r[f];
    if (v !== null && v !== undefined && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/**
 * Pure: what happened, in words — "Triage → SHA application due: “Have called
 * the patient.”". Returned as plain text; the view escapes it.
 * @param {{kind: string, fromStage?: string|null, toStage?: string|null, rows: any[]}} step
 * @param {{patientName?: string|null, showNames?: boolean}} [ctx]
 * @returns {string}
 */
export function describeStep(step, ctx) {
  const c = ctx || {};
  const hide = (/** @type {string} */ t) => (c.showNames ? t : maskName(t, c.patientName));
  const rows = (step && step.rows) || [];
  const reasonRaw = field(rows, 'reason');
  const reason = reasonRaw && reasonRaw !== DEFAULT_STAGE_REASON ? hide(reasonRaw) : '';
  const quote = reason ? `: “${reason}”` : '';
  switch (step && step.kind) {
    case 'stage':
      if (step.fromStage) return `${stageLabel(step.fromStage)} → ${stageLabel(step.toStage)}${quote}`;
      if (step.toStage) return `Moved to ${stageLabel(step.toStage)}${quote}`;
      // A legacy 'case_stage_changed' row carries no stages — its summary is
      // the only description there is (handled with the generic kinds below).
      return summaryText(step, rows, hide);
    case 'note': {
      if (rows.some(isStageRow)) return `Note at ${stageLabel(step.toStage)}${quote || ' (no text)'}`;
      return 'Updated the case notes';
    }
    case 'task': {
      const title = field(rows, 'task_title');
      return title ? `Created a task: ${hide(title)}` : 'Created a task';
    }
    case 'task_done':
      return 'Completed a task';
    case 'archive':
      return reasonRaw ? `Archived the patient — ${hide(words(reasonRaw))}` : 'Archived the patient';
    case 'consent':
      return 'Promoted a consent to a patient record';
    case 'case_created':
      return 'Created a patient record';
    case 'contact': {
      const out = field(rows, 'outcome');
      const ch = field(rows, 'channel');
      return out ? `Logged a contact — ${words(out)}${ch ? ` (${words(ch)})` : ''}` : 'Logged a contact';
    }
    default:
      return summaryText(step, rows, hide);
  }
}

/** Fallback words per kind, when a step has no summary to show. */
const KIND_WORDS = Object.freeze({
  stage: 'Moved the patient’s stage',
  assign: 'Assigned the patient',
  expense: 'Recorded an expense',
  ae: 'Reported an adverse event',
  admin: 'Changed a staff account',
  clinical: 'Updated clinical details',
  escalation: 'Raised an escalation',
  sha: 'Started an SHA application',
});

/**
 * The step's own summary (the app row's if there is one), actor prefix
 * dropped, capitalised, patient name masked by `hide`.
 * @param {any} step @param {any[]} rows @param {(t: string) => string} hide
 * @returns {string}
 */
function summaryText(step, rows, hide) {
  const app = rows.find((r) => r && r.event_source === 'app' && r.summary);
  const text = stripActor((app || rows.find((r) => r && r.summary) || {}).summary || '');
  const fallback = /** @type {Record<string, string>} */ (KIND_WORDS)[step && step.kind];
  return hide(text ? text[0].toUpperCase() + text.slice(1) : fallback || 'Changed a record');
}

// ─── Loaders (best-effort, never throw) ────────────────────────────────────

/**
 * Resolve case ids to {ref, name}: the loaded cases slice first, then ONE
 * minimal select per chunk for the rest. An id the database no longer returns
 * is marked gone (the FK is ON DELETE SET NULL, so this is rare — but a row can
 * outlive the case it names in a replayed or imported trail).
 * @param {string[]} ids
 * @param {{client?: any, cases?: any[]}} [deps]
 * @returns {Promise<Record<string, {ref: string|null, name: string|null, gone: boolean}>>}
 */
export async function resolveCases(ids, deps) {
  /** @type {Record<string, {ref: string|null, name: string|null, gone: boolean}>} */
  const out = {};
  const want = Array.from(new Set((ids || []).filter(Boolean)));
  if (!want.length) return out;
  let slice = [];
  try {
    slice = (deps && deps.cases) || allCases();
  } catch {
    slice = [];
  }
  const known = new Map();
  for (const c of slice || []) if (c && c.case_id) known.set(c.case_id, c);
  const missing = [];
  for (const id of want) {
    const c = known.get(id);
    if (c) out[id] = { ref: c.reference_number || null, name: c.patient_name || null, gone: false };
    else missing.push(id);
  }
  if (!missing.length) return out;
  let lookedUp = true;
  try {
    const client = (deps && deps.client) || getClient();
    for (let i = 0; i < missing.length; i += CASE_CHUNK) {
      const chunk = missing.slice(i, i + CASE_CHUNK);
      const { data, error } = await client
        .from(TABLES.CASES)
        .select('case_id,reference_number,patient_name')
        .in('case_id', chunk);
      if (error) {
        lookedUp = false;
        break;
      }
      for (const c of data || []) {
        if (c && c.case_id) out[c.case_id] = { ref: c.reference_number || null, name: c.patient_name || null, gone: false };
      }
    }
  } catch {
    lookedUp = false;
  }
  // Only call a case GONE when the lookup actually ran and did not return it.
  // A failed lookup leaves the reference blank, not a false "deleted".
  for (const id of missing) if (!out[id]) out[id] = { ref: null, name: null, gone: lookedUp };
  return out;
}

/**
 * Page through one window query until it runs dry or hits the cap.
 * @param {() => any} make returns a fresh builder (filters + order, no range)
 * @param {number} cap
 * @returns {Promise<{rows: any[], error: any, truncated: boolean}>}
 */
async function pageAll(make, cap) {
  const rows = [];
  for (let from = 0; from < cap; from += PAGE) {
    const { data, error } = await make().range(from, Math.min(from + PAGE, cap) - 1);
    if (error) return { rows: [], error, truncated: false };
    const batch = data || [];
    rows.push(...batch);
    if (batch.length < Math.min(PAGE, cap - from)) return { rows, error: null, truncated: false };
  }
  return { rows, error: null, truncated: true };
}

/**
 * Load the window: every STAFF audit row in [from, to) (light columns), the
 * scheduled job's row count, and the patient references for the cases seen.
 * The system count is best-effort on its own — if only that query fails, the
 * staff table still loads and the count reads as unknown (null).
 * @param {{from: string, to: string}} win ISO bounds, `to` exclusive
 * @param {{client?: any, cases?: any[], cap?: number}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 *   data: {rows, systemCount: number|null, cases, truncated, from, to}
 */
export async function loadActions(win, deps) {
  try {
    if (!win || !win.from || !win.to) return fail(new Error('no window'));
    const client = (deps && deps.client) || getClient();
    const cap = (deps && deps.cap) || ACTIONS_ROW_CAP;
    const staff = await pageAll(
      () =>
        client
          .from(TABLES.AUDIT_EVENTS)
          .select(ACTION_COLS)
          .gte('occurred_at', win.from)
          .lt('occurred_at', win.to)
          .not('actor_user_id', 'is', null)
          .order('occurred_at', { ascending: false })
          .order('event_id', { ascending: true }),
      cap
    );
    if (staff.error) return fail(staff.error);
    /** @type {number|null} */
    let systemCount = null;
    try {
      const sys = await client
        .from(TABLES.AUDIT_EVENTS)
        .select('event_id', { count: 'exact', head: true })
        .is('actor_user_id', null)
        .gte('occurred_at', win.from)
        .lt('occurred_at', win.to);
      if (!sys.error && typeof sys.count === 'number') systemCount = sys.count;
    } catch {
      systemCount = null;
    }
    const cases = await resolveCases(
      staff.rows.map((r) => r && r.case_id),
      { client, cases: deps && deps.cases }
    );
    return ok({ rows: staff.rows, systemCount, cases, truncated: staff.truncated, from: win.from, to: win.to });
  } catch (e) {
    return fail(e);
  }
}

/**
 * On demand: one person's rows for the window, newest first, capped at
 * PERSON_ROW_CAP, with the typed reason and description fields. Loaded only
 * when an administrator opens that person — free text stays off the wire
 * until someone asks to read it.
 * @param {{from: string, to: string, userId: string}} q
 * @param {{client?: any, cases?: any[], cap?: number}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 *   data: {rows, cases, capped}
 */
export async function loadPersonActions(q, deps) {
  try {
    if (!q || !q.from || !q.to || !q.userId) return fail(new Error('no person'));
    const client = (deps && deps.client) || getClient();
    const cap = (deps && deps.cap) || PERSON_ROW_CAP;
    const { data, error } = await client
      .from(TABLES.AUDIT_EVENTS)
      .select(PERSON_ACTION_COLS)
      .eq('actor_user_id', q.userId)
      .gte('occurred_at', q.from)
      .lt('occurred_at', q.to)
      .order('occurred_at', { ascending: false })
      .order('event_id', { ascending: true })
      .limit(cap);
    if (error) return fail(error);
    const rows = data || [];
    const cases = await resolveCases(
      rows.map((r) => r && r.case_id),
      { client, cases: deps && deps.cases }
    );
    return ok({ rows, cases, capped: rows.length >= cap });
  } catch (e) {
    return fail(e);
  }
}
